import { SET_ANSWER_TRIGGER, type GradingMode } from '@agavi/blocks';
import { useTranslation } from 'react-i18next';

import SelectMenu from '@/components/ui/select-menu';

/**
 * What a value block's targeted trigger MATCHES, outside graded.
 *
 * These blocks express an answer as a BAND, not as a listed entry, so unlike MCQ's options there
 * was nothing for a targeted state to point at. A teacher wanting to speak to their own answer
 * had to retype it as `exact:42`, which went stale the moment they edited it.
 *
 * A DROPDOWN, not a segmented bar: two entries today, and it was three buttons wide while optional
 * had a blank to offer, which cramped the condition editor beside it — the one thing on the card
 * that needs the room.
 *
 * The app's OWN dropdown, not a native `<select>`. Both exist and both are deliberate, and each
 * one's doc calls itself the task builder's convention — but a native select opens the operating
 * system's menu, so beside the custom trigger pickers it is visibly a different control in a
 * panel that is otherwise all Agavi. `SelectField` argues for native on accessibility grounds and
 * that argument is real; this one answers it with `role='listbox'`, arrow-key navigation and a
 * focus return, which is also what the student-facing Dropdown block runs on.
 *
 * SURVEY only. Graded shows nothing because the band already IS `correct` there, so the preset
 * would be a second name for it; optional shows nothing because it is inert and has no targeted
 * states at all — see `targetedFiresIn`.
 */
export type TriggerKind = 'answer' | 'custom';

export function triggerKindOf(trigger: string): TriggerKind {
  return trigger === SET_ANSWER_TRIGGER ? 'answer' : 'custom';
}

export function TriggerKindSelect({ trigger, gradingMode, onChange, customDefault, answerTaken = false }: {
  trigger: string;
  gradingMode: GradingMode;
  onChange: (trigger: string) => void;
  /** The trigger to restore when returning to Custom — the block's own empty condition. */
  customDefault: string;
  /**
   * Another card is ALREADY the Answer, so this one cannot also be.
   *
   * There is one authored answer, so two cards pointing at it are two cards with the same
   * condition: both fire together, every time, and the teacher gets a duplicate they then have to
   * work out how to undo. Nothing stopped it — the preset carries no value, so the overlap
   * detection every block runs over its targeted bands had nothing to compare.
   *
   * Disabled rather than hidden, and still disabled on the card that holds it (where it is the
   * current value and must stay selectable-looking, because `SelectField` renders the chosen
   * option): a missing entry reads as a feature that is gone, where a greyed one says the slot is
   * taken and can be freed by changing the other card.
   */
  answerTaken?: boolean;
}) {
  const { t } = useTranslation();
  if (gradingMode !== 'survey') return null;
  const kind = triggerKindOf(trigger);
  return (
    <SelectMenu
      ariaLabel={t('blocks.triggerKind.label')}
      value={kind}
      onChange={v => onChange(v === 'answer' ? SET_ANSWER_TRIGGER : customDefault)}
      options={[
        // Never disabled on the card that IS the answer — that option is its own current value,
        // and the trigger reads its label from the selected option.
        { value: 'answer', label: t('blocks.triggerKind.answer'), disabled: answerTaken && kind !== 'answer' },
        { value: 'custom', label: t('blocks.triggerKind.custom') },
      ]}
      fullWidth
      className='w-full'
    />
  );
}
